// src/pages/auth/verification-complete.tsx
// Point de convergence unique après le flux Didit — que l'utilisateur
// revienne d'un popup desktop ou d'une redirection complète mobile.
// Source de vérité : Firestore (via le webhook Didit), jamais les query
// params de retour (falsifiables, non garantis par tous les modes Didit).

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { CheckCircle2, Clock, XCircle, Loader2 } from 'lucide-react';
import type { VerificationRole } from '@/components/verification/DiditVerification';

const RESUME_KEY = 'didit_resume';

const PAYMENT_ROUTE: Partial<Record<VerificationRole, string>> = {
  consultant: '/consultant/payment',
  partner: '/partner/plans',
  // lender : pas d'étape de paiement — direction dashboard une fois approuvé
};

interface ResumeContext {
  role: VerificationRole;
  uid: string;
  sessionId: string | null;
  createdAt: number;
}

function readResumeContext(): ResumeContext | null {
  try {
    const raw = sessionStorage.getItem(RESUME_KEY);
    return raw ? (JSON.parse(raw) as ResumeContext) : null;
  } catch {
    return null;
  }
}

function clearResumeContext() {
  try {
    sessionStorage.removeItem(RESUME_KEY);
  } catch {
    /* ignore */
  }
}

const T = {
  en: {
    waitingTitle: 'Verification in Progress',
    waitingText: 'We are confirming your identity verification. This page will update automatically — please do not close it.',
    reviewTitle: 'Under Review',
    reviewText: 'Your verification is being reviewed by our team. You will receive an email notification once complete.',
    declinedTitle: 'Verification Declined',
    declinedText: 'Unfortunately your verification was declined. Please contact support for assistance.',
    contactSupport: 'Contact Support',
    noContextTitle: 'Verification Complete',
    noContextText: 'Redirecting you to your dashboard...',
  },
  fr: {
    waitingTitle: 'Vérification en cours',
    waitingText: "Nous confirmons votre vérification d'identité. Cette page se mettra à jour automatiquement — veuillez ne pas la fermer.",
    reviewTitle: 'En cours de révision',
    reviewText: 'Votre vérification est examinée par notre équipe. Vous recevrez une notification par courriel une fois complétée.',
    declinedTitle: 'Vérification refusée',
    declinedText: 'Malheureusement, votre vérification a été refusée. Veuillez contacter le support.',
    contactSupport: 'Contacter le support',
    noContextTitle: 'Vérification complétée',
    noContextText: 'Redirection vers votre tableau de bord...',
  },
};

type Phase = 'loading' | 'waiting' | 'review' | 'declined' | 'no-context';

export default function VerificationComplete() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { language } = useLanguage();
  const t = T[language === 'fr' ? 'fr' : 'en'];
  const [phase, setPhase] = useState<Phase>('loading');
  const isPopupWindow =
  typeof window !== 'undefined' && !!window.opener && window.opener !== window;

  useEffect(() => {
    if (authLoading) return;

    // Pas connecté (rare, mais possible si la session Firebase a expiré
    // pendant un long parcours Didit sur mobile) — on repasse par le login,
    // qui nous ramènera ici une fois authentifié.
    if (!user) {
      const next = encodeURIComponent('/verification-complete');
      navigate(`/login?next=${next}`, { replace: true });
      return;
    }

    const resume = readResumeContext();

    // Pas de contexte de reprise valide (arrivée hors flux, uid différent,
    // sessionStorage vidé) — RequireAccountActive prendra le relais depuis
    // le dashboard, qui sait déjà rediriger vers le paiement ou l'écran
    // d'attente approprié selon le statut réel de l'utilisateur.
    if (!resume || resume.uid !== user.uid) {
      setPhase('no-context');
      const timer = setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
      return () => clearTimeout(timer);
    }

    setPhase('waiting');

    const unsub = onSnapshot(
      doc(db, 'users', user.uid),
      (snap) => {
        if (!snap.exists()) return;
        const data = snap.data();
        const bgStatus = data?.backgroundCheckStatus;
        const diditStatus = data?.diditVerification?.status;
        const manualReviewStatus = data?.diditVerification?.manualReviewStatus;

        const approved = bgStatus === 'approved' || diditStatus === 'Approved';
        const declined =
          bgStatus === 'rejected' ||
          diditStatus === 'Declined' ||
          manualReviewStatus === 'declined';
        const inReview = diditStatus === 'In Review' && !manualReviewStatus;

        if (declined) {
          clearResumeContext();
          setPhase('declined');
          return;
        }

        if (inReview) {
          setPhase('review');
          return; // reste sur cette page, en attente de la décision staff
        }

        if (approved) {
          clearResumeContext();
          // NOUVEAU — si cette page tourne dans le popup ouvert par
          // DiditVerification.tsx (desktop), on se contente de le fermer.
          // C'est la fenêtre principale (via son propre onSnapshot dans
          // DiditVerification.tsx) qui doit être la seule à naviguer —
          // sinon les deux fenêtres naviguent en parallèle et peuvent
          // déclencher deux checkouts Stripe distincts.
          if (isPopupWindow) {
            window.close();
            return;
          }
          const paymentRoute = PAYMENT_ROUTE[resume.role];
          navigate(paymentRoute || '/dashboard', { replace: true });
        }
        // sinon (Not Started / In Progress) → on continue d'attendre
      },
      (error) => {
        console.error('[VerificationComplete] Firestore listener error:', error);
      }
    );

    return () => unsub();
  }, [user, authLoading, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 to-slate-200 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-xl p-8 text-center shadow-lg">
        {(phase === 'loading' || phase === 'waiting' || phase === 'no-context') && (
          <>
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Loader2 className="h-8 w-8 text-blue-600 animate-spin" />
            </div>
            <h1 className="text-xl font-bold text-[#1D314D] mb-2">
              {phase === 'no-context' ? t.noContextTitle : t.waitingTitle}
            </h1>
            <p className="text-slate-600">
              {phase === 'no-context' ? t.noContextText : t.waitingText}
            </p>
          </>
        )}

        {phase === 'review' && (
          <>
            <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Clock className="h-8 w-8 text-amber-600" />
            </div>
            <h1 className="text-xl font-bold text-[#1D314D] mb-2">{t.reviewTitle}</h1>
            <p className="text-slate-600">{t.reviewText}</p>
          </>
        )}

        {phase === 'declined' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            <h1 className="text-xl font-bold text-[#1D314D] mb-2">{t.declinedTitle}</h1>
            <p className="text-slate-600 mb-4">{t.declinedText}</p>
            <a
              href="mailto:support@acquizgroup.com"
              className="inline-block px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition"
            >
              {t.contactSupport}
            </a>
          </>
        )}
      </div>
    </div>
  );
}